import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Star, ChevronDown, ChevronUp, ExternalLink, 
  CheckCircle, Clock, AlertCircle, Filter, Download, Trash2,
  MessageSquare, Image as ImageIcon, Monitor, Calendar
} from 'lucide-react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';

// Star display component
const StarDisplay = ({ rating, size = 16 }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        size={size}
        className={star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 dark:text-gray-600'}
      />
    ))}
  </div>
);

// Status badge component
const StatusBadge = ({ status, onClick }) => {
  const statusConfig = {
    new: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', icon: Clock, label: 'New' },
    reviewed: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300', icon: AlertCircle, label: 'Reviewed' },
    addressed: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', icon: CheckCircle, label: 'Addressed' },
  };

  const config = statusConfig[status] || statusConfig.new;
  const Icon = config.icon;

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text} hover:opacity-80 transition-opacity`}
    >
      <Icon size={12} />
      {config.label}
    </button>
  );
};

// Individual feedback card
const FeedbackCard = ({ feedback, onStatusChange, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [showImage, setShowImage] = useState(false);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const cycleStatus = () => {
    const statuses = ['new', 'reviewed', 'addressed'];
    const currentIndex = statuses.indexOf(feedback.status || 'new');
    const nextStatus = statuses[(currentIndex + 1) % statuses.length];
    onStatusChange(feedback.id, nextStatus);
  };

  const avgRating = Object.values(feedback.ratings || {}).filter(r => r > 0).reduce((a, b, _, arr) => a + b / arr.length, 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-semibold text-gray-800 dark:text-gray-100 truncate">
                {feedback.userEmail}
              </p>
              <StatusBadge status={feedback.status} onClick={cycleStatus} />
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                {formatDate(feedback.timestamp)}
              </span>
              {feedback.groupName && (
                <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                  {feedback.groupName}
                </span>
              )}
            </div>
          </div>
          
          {/* Average Rating */}
          <div className="text-right">
            <div className="flex items-center gap-1">
              <Star size={20} className="fill-yellow-400 text-yellow-400" />
              <span className="text-lg font-bold text-gray-800 dark:text-gray-100">
                {avgRating.toFixed(1)}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">avg rating</p>
          </div>
        </div>
      </div>

      {/* Ratings Summary */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800/50 grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { key: 'overall', label: 'Overall' },
          { key: 'easeOfUse', label: 'Ease of Use' },
          { key: 'features', label: 'Features' },
          { key: 'design', label: 'Design' },
          { key: 'performance', label: 'Performance' },
        ].map(({ key, label }) => (
          <div key={key} className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
            <div className="flex justify-center">
              <StarDisplay rating={feedback.ratings?.[key] || 0} size={14} />
            </div>
          </div>
        ))}
      </div>

      {/* Expandable Content */}
      <div className="p-4">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium"
        >
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          {expanded ? 'Hide Details' : 'Show Details'}
          {(feedback.favoriteFeature || feedback.missingFeature || feedback.bugs || feedback.additionalNotes || feedback.screenshotUrl) && (
            <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-1.5 py-0.5 rounded text-xs">
              Has feedback
            </span>
          )}
        </button>

        {expanded && (
          <div className="mt-4 space-y-4">
            {/* Text Feedback */}
            {feedback.favoriteFeature && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
                  Favorite Feature
                </p>
                <p className="text-gray-800 dark:text-gray-200 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border-l-4 border-green-500">
                  {feedback.favoriteFeature}
                </p>
              </div>
            )}

            {feedback.missingFeature && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
                  Missing Feature Request
                </p>
                <p className="text-gray-800 dark:text-gray-200 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border-l-4 border-blue-500">
                  {feedback.missingFeature}
                </p>
              </div>
            )}

            {feedback.bugs && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
                  Bugs / Issues
                </p>
                <p className="text-gray-800 dark:text-gray-200 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border-l-4 border-red-500">
                  {feedback.bugs}
                </p>
              </div>
            )}

            {feedback.additionalNotes && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
                  Additional Notes
                </p>
                <p className="text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 p-3 rounded-lg">
                  {feedback.additionalNotes}
                </p>
              </div>
            )}

            {/* Screenshot */}
            {feedback.screenshotUrl && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                  Screenshot
                </p>
                <div className="relative">
                  <img
                    src={feedback.screenshotUrl}
                    alt="User screenshot"
                    className="w-full max-h-64 object-contain rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer"
                    onClick={() => setShowImage(true)}
                  />
                  <a
                    href={feedback.screenshotUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-lg hover:bg-black/70 transition-colors"
                  >
                    <ExternalLink size={16} />
                  </a>
                </div>
              </div>
            )}

            {/* Device Info */}
            <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                Device Info
              </p>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded flex items-center gap-1">
                  <Monitor size={12} />
                  {feedback.screenSize || 'Unknown'}
                </span>
                <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                  {feedback.platform || 'Unknown platform'}
                </span>
                <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                  v{feedback.appVersion || '1.0.0'}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => onDelete(feedback.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-sm"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Full Image Modal */}
      {showImage && feedback.screenshotUrl && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setShowImage(false)}
        >
          <img
            src={feedback.screenshotUrl}
            alt="Full screenshot"
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>
      )}
    </div>
  );
};

// Main Admin Page
const FeedbackAdmin = ({ onNavigate }) => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, new, reviewed, addressed
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, rating

  // Real-time listener for feedback
  useEffect(() => {
    const q = query(collection(db, 'feedback'), orderBy('timestamp', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const feedbackData = [];
      snapshot.forEach((doc) => {
        feedbackData.push({ id: doc.id, ...doc.data() });
      });
      setFeedback(feedbackData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching feedback:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Update status
  const handleStatusChange = async (feedbackId, newStatus) => {
    try {
      await updateDoc(doc(db, 'feedback', feedbackId), {
        status: newStatus
      });
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Delete feedback
  const handleDelete = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) return;
    
    try {
      await deleteDoc(doc(db, 'feedback', feedbackId));
    } catch (error) {
      console.error('Error deleting feedback:', error);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Email', 'Group', 'Overall', 'Ease of Use', 'Features', 'Design', 'Performance', 'Favorite', 'Missing', 'Bugs', 'Notes', 'Status'];
    
    const rows = feedback.map(f => [
      f.timestamp?.toDate?.().toISOString() || '',
      f.userEmail,
      f.groupName || '',
      f.ratings?.overall || 0,
      f.ratings?.easeOfUse || 0,
      f.ratings?.features || 0,
      f.ratings?.design || 0,
      f.ratings?.performance || 0,
      f.favoriteFeature || '',
      f.missingFeature || '',
      f.bugs || '',
      f.additionalNotes || '',
      f.status || 'new'
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `boply-feedback-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Filter and sort
  const filteredFeedback = feedback
    .filter(f => filter === 'all' || f.status === filter)
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0);
      } else if (sortBy === 'oldest') {
        return (a.timestamp?.seconds || 0) - (b.timestamp?.seconds || 0);
      } else if (sortBy === 'rating') {
        const avgA = Object.values(a.ratings || {}).filter(r => r > 0).reduce((sum, r, _, arr) => sum + r / arr.length, 0);
        const avgB = Object.values(b.ratings || {}).filter(r => r > 0).reduce((sum, r, _, arr) => sum + r / arr.length, 0);
        return avgB - avgA;
      }
      return 0;
    });

  // Stats
  const stats = {
    total: feedback.length,
    new: feedback.filter(f => f.status === 'new').length,
    avgRating: feedback.length > 0
      ? (feedback.reduce((sum, f) => {
          const avg = Object.values(f.ratings || {}).filter(r => r > 0).reduce((a, b, _, arr) => a + b / arr.length, 0);
          return sum + avg;
        }, 0) / feedback.length).toFixed(1)
      : 0
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => onNavigate('dashboard')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                  Beta Feedback
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {stats.total} responses • {stats.new} new
                </p>
              </div>
            </div>
            
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Download size={18} />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
            <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{stats.total}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Responses</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.new}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">New (Unread)</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
            <div className="flex items-center gap-1">
              <Star size={24} className="fill-yellow-400 text-yellow-400" />
              <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{stats.avgRating}</p>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Avg Rating</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm">
            {['all', 'new', 'reviewed', 'addressed'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  filter === status
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="rating">Highest Rated</option>
          </select>
        </div>

        {/* Feedback List */}
        {filteredFeedback.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-12 text-center shadow-md">
            <MessageSquare size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
              No feedback yet
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              {filter !== 'all' 
                ? `No ${filter} feedback found. Try changing the filter.`
                : 'Feedback from beta testers will appear here.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredFeedback.map((item) => (
              <FeedbackCard
                key={item.id}
                feedback={item}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackAdmin;